import { useCallback, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** `"9:16"` → `[9, 16]`; anything else → null. */
export function parseAspectRatio(
  ratio: string | null | undefined
): [number, number] | null {
  if (!ratio) return null;
  const match = /^\s*(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/.exec(ratio);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!(width > 0) || !(height > 0)) return null;
  return [width, height];
}

/**
 * The image a URL points at, ignoring how it is signed.
 *
 * Asset URLs are signed, and every poll of the ad or the graphic hands back a
 * fresh signature for the SAME picture. Treating each as a new image replayed
 * the fade-in on every poll — the row thumbnail "kept refreshing". Identity
 * is the path; the query string is only the key to fetch it with.
 */
export function imageIdentity(src: string | undefined): string | undefined {
  if (!src) return undefined;
  const at = src.indexOf('?');
  return at === -1 ? src : src.slice(0, at);
}

/**
 * Whether the image at `src` has finished downloading; resets when `src`
 * points at a different image (a new signature for the same one does not
 * count).
 *
 * `ref` is stable per image, so React attaches it once rather than detaching
 * and re-attaching a fresh closure on every render of the host (rows
 * re-render on every poll). Setting the already-set value bails out.
 */
export function useImageLoaded(src: string | undefined) {
  const identity = imageIdentity(src);
  const [loadedIdentity, setLoadedIdentity] = useState<string>();
  // A cached image can be complete before React attaches `onLoad`; read it
  // off the element so the frame does not wait for an event already fired.
  const ref = useCallback(
    (el: HTMLImageElement | null) => {
      if (el?.complete && el.naturalWidth > 0) setLoadedIdentity(identity);
    },
    [identity]
  );
  const onLoad = useCallback(() => setLoadedIdentity(identity), [identity]);
  return {
    loaded: Boolean(identity) && loadedIdentity === identity,
    onLoad,
    ref,
  };
}

/**
 * A rendered image arriving, in one frame that never changes element.
 *
 * The wait and the result used to be different elements: a pulsing box, then
 * — the moment the worker said "ready" — an `<img>` with no size until its
 * bytes arrived, which collapsed the space and then popped the picture in.
 * Here the frame is mounted once, at its final shape, and only its contents
 * change:
 *
 *   1. pulsing placeholder while the render runs (no `src` yet);
 *   2. the small thumbnail, heavily blurred, as soon as it is available —
 *      the picture is recognisable before it is sharp;
 *   3. the full image fading in from blurred to sharp over the top.
 *
 * It is always the full width of its column — the same width as whatever sits
 * under it (the ad's copy card), so the two read as one stack. While WAITING
 * its height is capped at 55vh: sized by width alone, a 9:16 box in a narrow
 * column is taller than the panel; it overflows, its rounded corners scroll
 * out of view, and the caption lands at the bottom of the page. Once the
 * image has loaded, the cap goes and its own proportions win, so a ratio the
 * API did not report never crops it.
 */
export function ImageReveal({
  src,
  previewSrc,
  alt = '',
  aspectRatio,
  caption,
  progress,
}: {
  /** The finished image. Absent while it is still being made. */
  src?: string | null;
  /** A small version (a thumbnail) to show blurred while `src` downloads. */
  previewSrc?: string | null;
  alt?: string;
  /** "1:1", "9:16", "4:5", … as the asset reports it. */
  aspectRatio: string | null | undefined;
  /** "Rendering — images can take a few minutes." Shown until the image is in. */
  caption?: string;
  /** 0–100 when the worker reports one; the bar is hidden otherwise. */
  progress?: number | null;
}) {
  const full = useImageLoaded(src ?? undefined);
  const preview = useImageLoaded(previewSrc ?? undefined);
  // The measured size remembers WHICH image it measured, so a replaced
  // creative starts its wait in the reported shape, not the shape of the
  // picture it replaces.
  const [measured, setMeasured] = useState<{
    src: string;
    size: [number, number];
  }>();
  const natural =
    measured && measured.src === imageIdentity(src ?? undefined)
      ? measured.size
      : undefined;
  const [width, height] = natural ?? parseAspectRatio(aspectRatio) ?? [1, 1];
  const showCaption = Boolean(caption) && !full.loaded;

  return (
    <div
      className="flex w-full max-w-sm flex-col items-center gap-3"
      data-testid="image-reveal"
    >
      {/* The height cap stays once the image is in — a frame that grew the
          moment the picture arrived would be the very jump this avoids. A
          portrait image in a short panel letterboxes inside the cap instead
          (`object-contain` below); in a tall enough panel the ratio and the
          cap agree and nothing is boxed. */}
      <div
        className="relative max-h-[55vh] w-full overflow-hidden rounded-lg border bg-muted"
        style={{ aspectRatio: `${width} / ${height}` }}
        aria-busy={!full.loaded}
      >
        {/* The skeleton's own pulse, in the skeleton's own colour — a pulse
            in the frame's colour over the frame is a pulse nobody can see. */}
        <Skeleton
          aria-hidden="true"
          className={cn(
            'absolute inset-0 rounded-none transition-opacity duration-500',
            (preview.loaded || full.loaded) && 'opacity-0'
          )}
        />
        {previewSrc ? (
          <img
            ref={preview.ref}
            src={previewSrc}
            alt=""
            aria-hidden="true"
            onLoad={preview.onLoad}
            className={cn(
              'absolute inset-0 size-full scale-110 object-cover blur-xl transition-opacity duration-500 motion-reduce:transition-none',
              preview.loaded && !full.loaded ? 'opacity-100' : 'opacity-0'
            )}
          />
        ) : null}
        {src ? (
          <img
            ref={full.ref}
            src={src}
            alt={alt}
            onLoad={(event) => {
              const img = event.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setMeasured({
                  src: imageIdentity(src ?? undefined) ?? '',
                  size: [img.naturalWidth, img.naturalHeight],
                });
              }
              full.onLoad();
            }}
            className={cn(
              'absolute inset-0 size-full object-contain transition-[opacity,filter,transform] duration-700 ease-out motion-reduce:transition-none',
              full.loaded
                ? 'scale-100 opacity-100 blur-0'
                : 'scale-105 opacity-0 blur-2xl'
            )}
          />
        ) : null}
      </div>
      {showCaption ? (
        <p className="animate-pulse text-xs text-muted-foreground">{caption}</p>
      ) : null}
      {showCaption && typeof progress === 'number' && progress > 0 ? (
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}
